import React, { useState, useEffect } from 'react';
import { Linkedin, Save } from 'lucide-react';
import XIcon from '../../components/icons/XIcon';

const SocialLinksManager = () => {
  const [links, setLinks] = useState({
    linkedin: '',
    twitter: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/social-links`);
      const data = await response.json();
      setLinks({ linkedin: data.linkedin || '', twitter: data.twitter || '' });
    } catch (error) {
      console.error('Error fetching social links:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/social-links`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(links),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Social media links updated successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Failed to update links. Please try again.');
      }
    } catch (error) {
      console.error('Error updating social links:', error);
      setMessage('Failed to update links. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (platform, value) => {
    setLinks(prev => ({
      ...prev,
      [platform]: value
    }));
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px', maxWidth: '800px' }}>
      <div style={{ marginBottom: '32px' }}>
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: '2rem',
            fontWeight: '700',
            marginBottom: '12px',
            color: '#111111'
          }}
        >
          Social Media Links
        </h2>
        <p style={{ color: '#666666', fontSize: '0.95rem' }}>
          Update your LinkedIn and X profile URLs. These power the icons on the Contact page and in the site footer.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* LinkedIn */}
          <div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#111111'
              }}
            >
              <Linkedin size={20} color="#0077B5" />
              LinkedIn Profile URL
            </label>
            <input
              type="url"
              value={links.linkedin}
              onChange={(e) => handleChange('linkedin', e.target.value)}
              placeholder="https://linkedin.com/in/your-profile"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'var(--font-body)'
              }}
            />
          </div>

          {/* Twitter/X */}
          <div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#111111'
              }}
            >
              <XIcon size={18} color="#111111" />
              X (Twitter) Profile URL
            </label>
            <input
              type="url"
              value={links.twitter}
              onChange={(e) => handleChange('twitter', e.target.value)}
              placeholder="https://twitter.com/your-handle"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'var(--font-body)'
              }}
            />
          </div>

          {/* YouTube removed: the public site now shows LinkedIn and X only */}
        </div>

        {message && (
          <div
            style={{
              marginTop: '24px',
              padding: '16px',
              backgroundColor: message.includes('success') ? '#E8F5E9' : '#FFEBEE',
              border: `1px solid ${message.includes('success') ? '#4CAF50' : '#F44336'}`,
              borderRadius: '8px',
              color: message.includes('success') ? '#2E7D32' : '#C62828'
            }}
          >
            {message}
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          style={{
            marginTop: '32px',
            padding: '14px 32px',
            backgroundColor: '#111111',
            color: '#F4F1E8',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            opacity: saving ? 0.6 : 1
          }}
        >
          <Save size={18} />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>

      <div
        style={{
          marginTop: '40px',
          padding: '20px',
          backgroundColor: '#F4F1E8',
          borderRadius: '8px',
          fontSize: '0.9rem',
          color: '#666666'
        }}
      >
        <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#111111' }}>
          Tips:
        </p>
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          <li>Use full URLs including "https://"</li>
          <li>The LinkedIn and X icons always show on the site; add a URL here to make them link to your profile</li>
          <li>Changes take effect immediately after saving</li>
        </ul>
      </div>
    </div>
  );
};

export default SocialLinksManager;
